// ==================== RESELLER COUPONS MODAL ====================
// Super admin: list, create, edit, (de)activate and delete one reseller's coupons.
// Every coupon setting is chosen here; customers keep the terms they got (snapshot).

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Checkbox, ConfirmDialog, Dropdown, Input, Modal } from '../ui';
import {
  resellersService,
  type Coupon,
  type CouponData,
  type ExistingCustomers,
  type Reseller,
} from '../../services/resellers.service';
import { DeactivateResellerDialog } from './DeactivateResellerDialog';
import {
  discountScopeLabel,
  durationLabel,
  eligibilityLabel,
  formatExpiryDate,
  formatPercent,
  isCouponExpired,
} from './labels';

const defaults: CouponData = {
  code: '',
  discountPercent: 20,
  discountScope: 'PLAN_ONLY',
  discountDuration: 'FIRST_PAYMENT',
  commissionPercent: 10,
  commissionDuration: 'FIRST_PAYMENT',
  allowExistingCustomers: false,
  appliesToOfflinePayments: false,
  expiresAt: null,
  maxUses: null,
  isActive: true,
};

interface ChoiceProps<T extends string> {
  label: string;
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

// A labelled pair of radio buttons
const Choices = <T extends string>({ label, value, options, onChange, disabled }: ChoiceProps<T>) => (
  <fieldset className="space-y-2 text-sm text-(--color-soft-gray)">
    <legend className="mb-2 font-medium">{label}</legend>
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {options.map(([key, text]) => (
        <label key={key} className="flex gap-2 items-center cursor-pointer">
          <input
            type="radio"
            name={label}
            value={key}
            checked={value === key}
            onChange={() => onChange(key)}
            disabled={disabled}
            className="accent-orange-400"
          />
          {text}
        </label>
      ))}
    </div>
  </fieldset>
);

interface CouponFormProps {
  coupon?: Coupon | null;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (data: CouponData) => void;
}

const CouponForm = ({ coupon, saving, error, onCancel, onSave }: CouponFormProps) => {
  const [data, setData] = useState<CouponData>(
    coupon
      ? {
          code: coupon.code,
          discountPercent: Number(coupon.discountPercent),
          discountScope: coupon.discountScope,
          discountDuration: coupon.discountDuration,
          commissionPercent: Number(coupon.commissionPercent),
          commissionDuration: coupon.commissionDuration,
          allowExistingCustomers: coupon.allowExistingCustomers,
          appliesToOfflinePayments: coupon.appliesToOfflinePayments,
          expiresAt: coupon.expiryDate ?? null,
          maxUses: coupon.maxUses,
          isActive: coupon.isActive,
        }
      : defaults,
  );
  const update = <K extends keyof CouponData>(key: K, value: CouponData[K]) =>
    setData((prev) => ({ ...prev, [key]: value }));
  const codeLocked = !!coupon?.uses;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave(data);
      }}
      className="space-y-5"
    >
      <Input
        label="Coupon code"
        placeholder="e.g., PARTNER20"
        value={data.code}
        onChange={(event) => update('code', event.target.value.toUpperCase())}
        required
        minLength={3}
        maxLength={30}
        pattern={String.raw`[A-Za-z0-9\-]{3,30}`}
        disabled={saving || codeLocked}
        hint={codeLocked ? 'The code cannot change after its first use.' : '3–30 letters, digits or dashes.'}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          label="Discount %"
          type="number"
          min={1}
          max={99}
          step="0.01"
          required
          value={data.discountPercent}
          onChange={(event) => update('discountPercent', Number(event.target.value))}
          disabled={saving}
          hint="1–99% of what the discount covers"
        />
        <Input
          label="Commission %"
          type="number"
          min={0}
          max={100}
          step="0.01"
          required
          value={data.commissionPercent}
          onChange={(event) => update('commissionPercent', Number(event.target.value))}
          disabled={saving}
          hint="Of what the customer actually paid"
        />
      </div>

      <Choices
        label="Discount applies to"
        value={data.discountDuration}
        options={[
          ['FIRST_PAYMENT', 'First payment only'],
          ['EVERY_PAYMENT', 'Every recurring payment'],
        ]}
        onChange={(value) => update('discountDuration', value)}
        disabled={saving}
      />
      <Choices
        label="Discount covers"
        value={data.discountScope}
        options={[
          ['PLAN_ONLY', 'Plan only'],
          ['WHOLE_PAYMENT', 'Whole payment (setup fee, Lead Gen and extra accounts included)'],
        ]}
        onChange={(value) => update('discountScope', value)}
        disabled={saving}
      />
      <Choices
        label="Commission on"
        value={data.commissionDuration}
        options={[
          ['FIRST_PAYMENT', 'First payment only'],
          ['EVERY_PAYMENT', 'Every recurring payment'],
        ]}
        onChange={(value) => update('commissionDuration', value)}
        disabled={saving}
      />
      <Choices
        label="Who can use it"
        value={data.allowExistingCustomers ? 'EXISTING' : 'NEW'}
        options={[
          ['NEW', 'New customers only'],
          ['EXISTING', 'Existing customers too'],
        ]}
        onChange={(value) => update('allowExistingCustomers', value === 'EXISTING')}
        disabled={saving}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          label="Expiry date (optional)"
          type="date"
          value={data.expiresAt ?? ''}
          onChange={(event) => update('expiresAt', event.target.value || null)}
          disabled={saving}
          hint="Valid through the end of this day"
        />
        <div className="space-y-3">
          <Checkbox
            label="Unlimited uses"
            checked={data.maxUses === null}
            onChange={(event) => update('maxUses', event.target.checked ? null : 1)}
            disabled={saving}
          />
          {data.maxUses !== null && (
            <Input
              label="Maximum uses (customers)"
              type="number"
              min={1}
              step={1}
              required
              value={data.maxUses}
              onChange={(event) => update('maxUses', Number(event.target.value))}
              disabled={saving}
            />
          )}
        </div>
      </div>

      <div className="space-y-3">
        <Checkbox
          label="Also applies to payments recorded by the super admin"
          checked={data.appliesToOfflinePayments}
          onChange={(event) => update('appliesToOfflinePayments', event.target.checked)}
          disabled={saving}
        />
        <Checkbox
          label="Active"
          checked={data.isActive}
          onChange={(event) => update('isActive', event.target.checked)}
          disabled={saving}
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-(--color-error)">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-(--color-border-gray)">
        <Button variant="secondary" type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" isLoading={saving}>
          {coupon ? 'Save Coupon' : 'Create Coupon'}
        </Button>
      </div>
    </form>
  );
};

interface ResellerCouponsModalProps {
  reseller: Reseller;
  onClose: () => void;
  onUpdated: () => void;
}

export const ResellerCouponsModal = ({ reseller, onClose, onUpdated }: ResellerCouponsModalProps) => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  // undefined = list, null = new coupon form, Coupon = edit form
  const [editing, setEditing] = useState<Coupon | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<{ coupon: Coupon; data: Partial<CouponData> } | null>(null);
  const [deleting, setDeleting] = useState<Coupon | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const response = await resellersService.getCoupons(reseller.id);
    if (response.success && response.data) setCoupons(response.data);
    else setError(response.message);
    setLoading(false);
  }, [reseller.id]);

  useEffect(() => {
    void Promise.resolve().then(load);
  }, [load]);

  const save = async (data: Partial<CouponData>, coupon?: Coupon | null) => {
    setSaving(true);
    setError(null);
    const response = coupon
      ? await resellersService.updateCoupon(reseller.id, coupon.id, data)
      : await resellersService.createCoupon(reseller.id, data as CouponData);
    setSaving(false);
    if (response.success) {
      if (coupon && data.existingCustomers) {
        const affected = (response.data as { affectedCustomers?: number } | undefined)?.affectedCustomers ?? 0;
        setNotice(
          data.existingCustomers === 'REMOVE'
            ? `${coupon.code} deactivated. ${affected} customer${affected === 1 ? '' : 's'} lost it.`
            : `${coupon.code} deactivated. Existing customers keep it.`,
        );
      } else {
        setNotice(null);
      }
      setEditing(undefined);
      setDeactivating(null);
      await load();
      onUpdated();
    } else {
      setError(response.message);
    }
  };

  const submit = (data: CouponData) => {
    // Switching an active coupon off asks what happens to its current customers first
    if (editing?.isActive && !data.isActive) {
      setError(null);
      setDeactivating({ coupon: editing, data });
      return;
    }
    // An inactive coupon that stays inactive is saved without isActive: the API asks
    // for the Keep/Remove choice on every switch-off.
    const { isActive, ...settings } = data;
    void save(editing && !editing.isActive && !isActive ? settings : data, editing);
  };

  const deactivate = (existingCustomers: ExistingCustomers) =>
    deactivating ? save({ ...deactivating.data, existingCustomers }, deactivating.coupon) : undefined;

  const remove = async () => {
    if (!deleting) return;
    setSaving(true);
    setError(null);
    const response = await resellersService.deleteCoupon(reseller.id, deleting.id);
    setSaving(false);
    if (response.success) {
      setDeleting(null);
      await load();
      onUpdated();
    } else {
      setError(response.message);
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={() => !saving && onClose()}
        title={editing === undefined ? 'Coupons' : editing ? `Edit ${editing.code}` : 'Create Coupon'}
        subtitle={`${reseller.firstName} ${reseller.lastName}${reseller.companyName ? ` · ${reseller.companyName}` : ''}`}
        size="xl"
        contentClassName="p-6"
      >
        {editing !== undefined ? (
          <CouponForm
            key={editing?.id ?? 'new'}
            coupon={editing}
            saving={saving}
            error={error}
            onCancel={() => {
              setEditing(undefined);
              setError(null);
            }}
            onSave={submit}
          />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-(--color-muted-gray)">
                Edits and expiry only affect new uses; customers keep the terms they got.
              </p>
              <Button
                onClick={() => {
                  setEditing(null);
                  setError(null);
                }}
              >
                Create Coupon
              </Button>
            </div>
            {error && !deactivating && !deleting && (
              <p role="alert" className="text-sm text-(--color-error)">
                {error}
              </p>
            )}
            {notice && <p className="text-sm text-(--color-success)">{notice}</p>}
            {loading ? (
              <p className="text-(--color-muted-gray)">Loading coupons...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-(--color-muted-gray) border-b border-(--color-border-gray)">
                    <tr>
                      {['Code', 'Discount', 'Commission', 'Uses', 'Current customers', 'Expiry', 'Status', ''].map(
                        (text) => (
                          <th key={text} className="p-3 font-medium whitespace-nowrap">
                            {text}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-(--color-border-gray)">
                    {coupons.map((coupon) => (
                      <tr key={coupon.id} className="text-(--color-soft-gray)">
                        <td className="p-3">
                          <span className="text-white font-medium">{coupon.code}</span>
                          <span className="block text-xs text-(--color-muted-gray)">
                            {eligibilityLabel(coupon.allowExistingCustomers)}
                            {coupon.appliesToOfflinePayments && ' · admin payments too'}
                          </span>
                        </td>
                        <td className="p-3">
                          {formatPercent(coupon.discountPercent)}
                          <span className="block text-xs text-(--color-muted-gray)">
                            {discountScopeLabel(coupon.discountScope)} · {durationLabel(coupon.discountDuration)}
                          </span>
                        </td>
                        <td className="p-3">
                          {formatPercent(coupon.commissionPercent)}
                          <span className="block text-xs text-(--color-muted-gray)">
                            {durationLabel(coupon.commissionDuration)}
                          </span>
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          {coupon.uses} / {coupon.maxUses ?? 'Unlimited'}
                        </td>
                        <td className="p-3">{coupon.currentCustomers}</td>
                        <td className="p-3 whitespace-nowrap">{formatExpiryDate(coupon.expiryDate)}</td>
                        <td className="p-3">
                          <Badge variant={coupon.isActive && !isCouponExpired(coupon) ? 'success' : 'default'}>
                            {coupon.isActive ? (isCouponExpired(coupon) ? 'Expired' : 'Active') : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Dropdown
                            disabled={saving}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => {
                                  setEditing(coupon);
                                  setError(null);
                                },
                              },
                              {
                                key: 'active',
                                label: coupon.isActive ? 'Deactivate' : 'Activate',
                                onClick: () => {
                                  setError(null);
                                  if (coupon.isActive) setDeactivating({ coupon, data: { isActive: false } });
                                  else void save({ isActive: true }, coupon);
                                },
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                disabled: coupon.uses > 0,
                                colorClass: 'text-(--color-error)',
                                onClick: () => {
                                  setDeleting(coupon);
                                  setError(null);
                                },
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                    {coupons.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-(--color-muted-gray)">
                          No coupons yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>

      {deactivating && (
        <DeactivateResellerDialog
          title={`Deactivate ${deactivating.coupon.code}?`}
          effect="New customers can no longer use this coupon."
          currentCustomers={deactivating.coupon.currentCustomers}
          isLoading={saving}
          error={error}
          onClose={() => !saving && setDeactivating(null)}
          onConfirm={deactivate}
        />
      )}
      <ConfirmDialog
        isOpen={!!deleting}
        title="Delete Coupon?"
        description={
          <div>
            <p>Delete {deleting?.code}? This cannot be undone.</p>
            {error && <p className="text-(--color-error)">{error}</p>}
          </div>
        }
        variant="danger"
        confirmText="Delete"
        isLoading={saving}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
      />
    </>
  );
};
